import React from "react";

import { serializeError } from "serialize-error";

import snapchatLogo from "@assets/snapchat-logo.svg";
import { useUser } from "@components/user-context";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentWithFrontendIntegrationProps,
} from "@pages/settings/connection-status-manager";
import { type CardProps2 } from "@pages/settings/core";
import { PrimaryButton } from "@shared/buttons";
import { extractError } from "@utils/index";
import { launchOAuthPopup } from "@utils/oauth2-handler";

import { type AccountMetadataWithDescription } from "../account-selector";

export function SnapchatConnectionCard(props: CardProps2) {
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Snapchat Ads"
      connectionCallToAction={`
        Integrate your Snapchat Ads data to start receiving insights about
        your Snapchat ad spend.
      `}
      logoUrl={snapchatLogo}
      logoAltText="Snapchat Ads Logo"
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-snapchat-ads"
    >
      {({ setStatus, accounts }: LoginComponentChildrenProps) => (
        <SnapchatLoginComponent
          setStatus={setStatus}
          accounts={accounts}
          integrationDetails={props.integrationDetails}
        />
      )}
    </ConnectionCard>
  );
}

function SnapchatLoginComponent({
  setStatus,
  integrationDetails,
}: LoginComponentWithFrontendIntegrationProps) {
  const { user } = useUser();
  const [isButtonDisabled, setButtonDisabled] = React.useState(false);

  const { snapchatAuthBlob } = integrationDetails;

  const onConnectClicked = React.useCallback(async () => {
    try {
      setButtonDisabled(true);

      const tokenPayload = await openSnapchatPopup(
        snapchatAuthBlob.client_id,
        snapchatAuthBlob.scope,
      );

      const result = await user.callApi<{
        error: string;
        accounts: AccountMetadataWithDescription[];
      }>("/api/callbacks/snapchat", {
        method: "post",
        data: {
          tokenPayload,
          redirectURI: `${window.location.origin}/callbacks/snap`,
        },
      });

      if (result.error) {
        setStatus({
          type: ConnectionStatusType.HAS_ERROR,
          errorMessage: `An error occurred: ${serializeError(
            extractError(result.error),
          )}`,
        });
      } else {
        setStatus({
          type: ConnectionStatusType.SELECTING_ACCOUNT,
          accountList: result.accounts,
        });
      }
    } catch (e) {
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage: `An error occurred: ${serializeError(extractError(e))}`,
      });
    }

    setButtonDisabled(false);
  }, [setStatus, setButtonDisabled, user, snapchatAuthBlob]);

  return (
    <div className="mb-3">
      <div className="row">
        <div className="col-auto">
          <PrimaryButton disabled={isButtonDisabled} onClick={onConnectClicked}>
            {isButtonDisabled ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

async function openSnapchatPopup(oauthClientId: string, oauthScopes: string) {
  const params = new URLSearchParams();

  params.append("client_id", oauthClientId);
  params.append("redirect_uri", `${window.location.origin}/callbacks/snap`);
  params.append("response_type", "code");
  params.append("scope", oauthScopes);

  return launchOAuthPopup(
    `https://accounts.snapchat.com/login/oauth2/authorize?${params.toString()}`,
  );
}

const type = ConnectionType.integration_snapchat;
